import React from "react";
import { Upload } from "react-bootstrap-icons";
import Accordion from "react-bootstrap/Accordion";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import ProgressBar from "react-bootstrap/ProgressBar";
import { Execution, Video, VideoRecord } from "../../types";
import { getExecutionStatus, getLatestExecution } from "../../utils/executionStatuses";
import RecordDownloadComponent from "./RecordDownloadComponent";
import RecordExecutions from "./RecordExecutions";
import VideoList from "./VideoList";

interface RecordItemProps {
  record: VideoRecord;
  onSetMasterVideo: (record: VideoRecord, videoId: number) => Promise<void>;
  onUploadVideo: (record: VideoRecord) => void;
  recordDownloadKey: string;
  onSelectErrorExecution?: (execution: Execution) => void;
  isChecked?: boolean;
  onCheckChange?: (recordId: number, checked: boolean) => void;
  onExecutionUpdated: (recordId: number, execution: Execution) => void;
}

const RecordItem: React.FC<RecordItemProps> = ({
  record,
  onSetMasterVideo,
  onUploadVideo,
  recordDownloadKey,
  isChecked = false,
  onCheckChange,
  onExecutionUpdated
  //onSelectErrorExecution
}) => {


  const handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onCheckChange) {
      onCheckChange(record.id, e.target.checked);
    }
  };

  const handleExecutionUpdated = (execution: Execution) => {
    onExecutionUpdated(record.id, execution);
  }

  // Only surface the sub-status when the latest execution is the one in error —
  // a newer, still-running execution supersedes a stale error from an earlier attempt.
  const latestExecution = getLatestExecution(record);
  const errorSubStatuses =
    latestExecution?.status?.toLowerCase() === 'error' && latestExecution.sub_status
      ? [latestExecution.sub_status]
      : [];

  const uploadStatus = (() => {
    const videos = record.videos;
    if (videos.length === 0) return null;
    const allUploaded = videos.every((v: Video) => v.is_uploaded);
    const totalSize = videos.reduce((sum: number, v: Video) => sum + (v.size || 0), 0);
    if (totalSize === 0) return null;
    const uploadedSize = videos.reduce((sum: number, v: Video) => sum + (v.is_uploaded ? (v.size || 0) : (v.uploaded_size || 0)), 0);
    const percent = Math.min(100, Math.round((uploadedSize / totalSize) * 100));
    return { percent, allUploaded };
  })();

  return (
    <Accordion className="mb-3">
      <Card>
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <div className="d-flex align-items-center w-100">
              {/* Checkbox */}
              <Form.Check
                type="checkbox"
                className="me-3"
                checked={isChecked}
                onChange={handleCheckChange}
                onClick={(e) => e.stopPropagation()} // Prevent accordion toggle when clicking checkbox
              />

              {/* Record Info */}
              <div className="flex-grow-1">
                <h6 className="mb-0 d-flex align-items-center">
                  {record.name}
                  <Badge
                    bg={
                      record.type === 'PANORAMA' ? 'success' :
                        record.type === 'PHOTO' ? 'primary' : 'secondary'
                    }
                    className="ms-2"
                  >
                    {record.type}
                  </Badge>
                </h6>
                <div className="record-meta text-muted small">
                  <div>Created: {new Date(record.created_at).toLocaleString()}</div>
                  {record.user_email && <div>Created by: {record.user_email}</div>}
                  <div>srid: {record.srid}</div>
                </div>
                {errorSubStatuses.length > 0 && (
                  <div className="mt-1">
                    {errorSubStatuses.map((subStatus) => (
                      <Badge
                        key={subStatus}
                        bg="danger"
                        className="me-1"
                        title={`Execution error detail: ${subStatus}`}
                      >
                        {subStatus}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Upload progress */}
              {uploadStatus !== null && (
                <div className="mx-3 flex-grow-1" style={{ minWidth: 120, maxWidth: 200 }}>
                  <div className={`small mb-1 ${uploadStatus.allUploaded ? 'text-success fw-semibold' : 'text-muted'}`}>
                    {uploadStatus.allUploaded ? 'Uploaded' : `Uploading… ${uploadStatus.percent}%`}
                  </div>
                  <ProgressBar
                    now={uploadStatus.percent}
                    variant={uploadStatus.allUploaded ? 'success' : 'info'}
                    style={{ height: 6 }}
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="record-actions">
                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent accordion toggle
                      onUploadVideo(record);
                    }}
                    disabled={getExecutionStatus(record) != "none"}
                    className="d-flex align-items-center gap-1"
                  >
                    <Upload size={14} />
                    Upload Videos
                  </Button>
                </div>
              </div>
            </div>
          </Accordion.Header>

          <Accordion.Body>
            {/* Executions */}
            {record.executions.length > 0 && (
              <div className="mb-3">
                <RecordExecutions
                  executions={record.executions || []}
                  onExecutionUpdated={handleExecutionUpdated}
                />
              </div>
            )}

            {/* Videos */}
            {record.videos.length > 0 && (
              <div className="mb-3">
                <VideoList
                  videos={record.videos}
                  onSetMaster={onSetMasterVideo}
                  record={record}
                />
              </div>
            )}

            {/* Download Components */}
            {record.videos.length > 0 && (
              <div className="mt-4">
                <RecordDownloadComponent
                  recordId={record.id}
                  containerType="raw"
                  name="Raw Files"
                  tooltip="Click to view and download raw files (videos) for this record"
                  key={`download-raw-${record.id}-${recordDownloadKey}`}
                />
                <RecordDownloadComponent
                  recordId={record.id}
                  containerType="extracted"
                  name="Extracted Files"
                  tooltip="Click to view and download extracted files (frames, trajectory, result) for this record"
                  key={`download-extracted-${record.id}-${recordDownloadKey}`}
                />
                <RecordDownloadComponent
                  recordId={record.id}
                  containerType="processed"
                  name="Processed Files"
                  tooltip="Click to view and download processed files (frames, trajectory, result) for this record"
                  key={`download-processed-${record.id}-${recordDownloadKey}`}
                />
                <RecordDownloadComponent
                  recordId={record.id}
                  containerType="l2r"
                  name="L2R Files"
                  tooltip="Click to view and download L2R deliverables (trajectory, vpng, result, 1D export) for this record"
                  key={`download-l2r-${record.id}-${recordDownloadKey}`}
                />
              </div>
            )}
          </Accordion.Body>
        </Accordion.Item>
      </Card>
    </Accordion>
  );
};

export default RecordItem;
